import { geminiService } from "./geminiService";

import { supabase } from './supabase';
import { Plant, UserProfile, PlantStatus, PlantClassification, PlantKingdom } from '../types';
import { GoogleGenAI, Type } from "@google/genai";

export interface SmartActionResponse {
  action: 'create_reminder' | 'research' | 'none';
  datetime: string | null;
  message: string;
  confidence: number;
  sources?: { uri: string; title: string }[];
}

export interface UpdateUserResult {
  success: boolean;
  schemaWarning?: boolean;
  error?: string;
}

class StorageService {
  private getAI() {
    // Initializing with named parameter and robust environment variable fallbacks
    const apiKey = process.env.GEMINI_API_KEY || 
                   process.env.API_KEY || 
                   (import.meta.env?.VITE_GEMINI_API_KEY as string) || 
                   (import.meta.env?.VITE_API_KEY as string);
    return new GoogleGenAI({ apiKey });
  }

  async getUserId(): Promise<string | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.user?.id || null;
    } catch {
      return null;
    }
  }

  private mapPlant(p: any): Plant {
    return {
      id: p.id,
      userId: p.user_id,
      nomeComum: p.nome_comum || 'Planta sem nome',
      nomeCientifico: p.nome_cientifico || '',
      genero: p.genero || '',
      familia: p.familia || '',
      categoria: (p.categoria || PlantClassification.TROPICAL_FOLIAGE) as PlantClassification,
      reinoPlantae: p.reino_plantae as PlantKingdom || undefined, 
      dataAquisicao: p.data_aquisicao || new Date().toISOString(),
      origem: p.origem || 'comprada',
      observacoes: p.observacoes || '',
      planoManejo: p.plano_manejo || { rega: '', adubacao: '', poda: '' },
      tratamento: p.tratamento || { medicacao: '', controlePragas: '', instrucoesEspeciais: '' },
      status: (p.status || PlantStatus.HEALTHY) as PlantStatus,
      localizacao: p.localizacao || { latitude: 0, longitude: 0 },
      imagens: p.imagens || [],
      reminders: p.reminders || { watering: false, fertilizing: false, pruning: false, repotting: false },
      // Map database field ultima_rega to Plant.ultimaRega
      ultimaRega: p.ultima_rega,
      criadoEm: p.created_at,
      atualizadoEm: p.updated_at
    };
  }

  async ensureUserExists(user: any, extraData: any = {}): Promise<void> {
    if (!user?.id) return;
    try {
      const metadata = user.user_metadata || {};
      
      const profileData: any = {
        id: user.id,
        nome: extraData.nome || metadata.full_name || metadata.name || metadata.nome || user.email?.split('@')[0] || 'Jardineiro Raizama',
        email: user.email,
        updated_at: new Date().toISOString(),
        preferencias: extraData.preferencias || metadata.preferencias || { tema: 'dark', idioma: 'pt-BR' }
      };

      // Tenta pegar de extraData ou de metadata (caso venha do Auth)
      const idade = extraData.idade || metadata.idade;
      const telefone = extraData.telefone || metadata.telefone;
      const cpf = extraData.cpf || metadata.cpf;
      const endereco = extraData.endereco || metadata.endereco;
      const cidade = extraData.cidade || metadata.cidade;
      const estado = extraData.estado || metadata.estado;
      const cep = extraData.cep || metadata.cep;
      const photoUrl = extraData.photoUrl || metadata.photoUrl || metadata.avatar_url || metadata.picture;

      if (idade) profileData.idade = idade;
      if (telefone) profileData.telefone = telefone;
      if (cpf) profileData.cpf = cpf;
      if (endereco) profileData.endereco = endereco;
      if (cidade) profileData.cidade = cidade;
      if (estado) profileData.estado = estado;
      if (cep) profileData.cep = cep;
      if (photoUrl) profileData.photo_url = photoUrl;
      
      const { error } = await supabase
        .from('profiles')
        .upsert(profileData, { onConflict: 'id' });

      if (error) {
        console.error("[SUPABASE] Erro no Upsert de Perfil:", error.message);
      }
    } catch (err) {
      console.error("[Raizama] Falha crítica na sincronização de perfil:", err);
    }
  }

  async getUser(): Promise<UserProfile | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      
      if (!user) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.warn("[SUPABASE] Falha ao ler perfil:", error.message);
      }

      const metadata = user.user_metadata || {};
      
      return {
        uid: user.id,
        nome: data?.nome || metadata.full_name || metadata.name || user.email?.split('@')[0] || 'Usuário',
        email: user.email || data?.email || '',
        dataCriacao: data?.created_at || user.created_at,
        preferencias: data?.preferencias || { tema: 'dark', idioma: 'pt-BR' },
        idade: data?.idade || '',
        cpf: data?.cpf || '',
        telefone: data?.telefone || '',
        endereco: data?.endereco || '',
        cidade: data?.cidade || '',
        estado: data?.estado || '',
        cep: data?.cep || '',
        photoUrl: data?.photo_url || metadata.avatar_url || metadata.picture || null
      };
    } catch (e) {
      console.error("Erro crítico ao carregar perfil:", e);
      return null;
    }
  }

  async uploadAvatar(userId: string, base64: string): Promise<string> {
    const path = `${userId}/avatar_${Date.now()}.jpg`;
    const res = await fetch(base64);
    const blob = await res.blob();
    
    const { data, error } = await supabase.storage
      .from('profiles') 
      .upload(path, blob, { 
        contentType: 'image/webp', 
        upsert: true 
      });
      
    if (error) throw error;
    const { data: { publicUrl } } = supabase.storage.from('profiles').getPublicUrl(data.path);
    return publicUrl;
  }

  async updateUser(data: Partial<UserProfile>): Promise<UpdateUserResult> {
    const userId = await this.getUserId();
    if (!userId) return { success: false, error: "Usuário não autenticado" };

    let finalPhotoUrl = data.photoUrl;
    if (finalPhotoUrl && finalPhotoUrl.startsWith('data:')) {
      try {
        finalPhotoUrl = await this.uploadAvatar(userId, finalPhotoUrl);
      } catch (e) {
        console.error("[Raizama] Avatar upload failed:", e);
      }
    }
    
    // Usamos upsert para garantir que o registro exista e seja atualizado corretamente
    const updatePayload: any = {
      id: userId,
      updated_at: new Date().toISOString()
    };
    
    if (data.nome !== undefined) {
      updatePayload.nome = data.nome;
      try {
        await supabase.auth.updateUser({ data: { full_name: data.nome } });
      } catch (e) {
        console.warn("[Raizama] Falha ao sincronizar nome no Auth:", e);
      }
    }
    
    if (data.idade !== undefined) updatePayload.idade = data.idade;
    if (data.email !== undefined) updatePayload.email = data.email;
    if (data.cpf !== undefined) updatePayload.cpf = data.cpf;
    if (data.telefone !== undefined) updatePayload.telefone = data.telefone;
    if (data.endereco !== undefined) updatePayload.endereco = data.endereco;
    if (data.cidade !== undefined) updatePayload.cidade = data.cidade;
    if (data.estado !== undefined) updatePayload.estado = data.estado;
    if (data.cep !== undefined) updatePayload.cep = data.cep;
    if (finalPhotoUrl !== undefined) updatePayload.photo_url = finalPhotoUrl;
    if (data.preferencias !== undefined) updatePayload.preferencias = data.preferencias;

    const { error } = await supabase
      .from('profiles')
      .upsert(updatePayload, { onConflict: 'id' });
      
    if (error) {
      console.error("[Raizama] Erro ao atualizar perfil:", error.message);
      return { success: false, error: error.message };
    }
    
    return { success: true };
  }

  async updatePassword(password: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  }

  async updateEmail(email: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({ email });
    if (error) throw error;
  }

  async logout(): Promise<void> {
    await supabase.auth.signOut();
  }

  async deleteAccount(): Promise<void> {
    const userId = await this.getUserId();
    if (!userId) throw new Error("Usuário não identificado.");

    try {
      // 1. Limpar Imagens de Plantas no Storage
      const { data: plantFiles } = await supabase.storage.from('plants').list(userId);
      if (plantFiles && plantFiles.length > 0) {
        await supabase.storage.from('plants').remove(plantFiles.map(f => `${userId}/${f.name}`));
      }

      // 2. Limpar Avatares no Storage
      const { data: profileFiles } = await supabase.storage.from('profiles').list(userId);
      if (profileFiles && profileFiles.length > 0) {
        await supabase.storage.from('profiles').remove(profileFiles.map(f => `${userId}/${f.name}`));
      }

      // 3. Excluir registros do banco de dados
      // Deletamos as plantas primeiro devido a possíveis dependências
      const { error: plantsError } = await supabase.from('plants').delete().eq('user_id', userId);
      if (plantsError) throw new Error(`Erro ao excluir plantas: ${plantsError.message}`);

      const { error: profileError } = await supabase.from('profiles').delete().eq('id', userId);
      if (profileError) throw new Error(`Erro ao excluir perfil: ${profileError.message}`);

      // 4. Limpar dados locais do navegador
      localStorage.clear();
      sessionStorage.clear();

      // 5. Logout (Isso encerra a sessão no cliente)
      await this.logout();
      
      // Nota: A exclusão do usuário da tabela auth.users do Supabase geralmente requer 
      // privilégios de admin (Service Role) ou uma Edge Function dedicada.
      // O código acima garante que todos os dados pessoais e arquivos sejam removidos.
    } catch (err: any) {
      console.error("[Raizama] Falha ao excluir conta:", err);
      throw err;
    }
  }

  async getPlants(): Promise<Plant[]> {
    const userId = await this.getUserId();
    if (!userId) return [];
    try {
      const { data, error } = await supabase
        .from('plants')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (error) return [];
      return data.map(p => this.mapPlant(p));
    } catch {
      return [];
    }
  }

  async getPlantById(id: string): Promise<Plant | undefined> {
    const userId = await this.getUserId();
    if (!userId) return undefined;
    try {
      const { data, error } = await supabase
        .from('plants')
        .select('*')
        .eq('id', id)
        .eq('user_id', userId)
        .maybeSingle();

      if (error || !data) return undefined;
      return this.mapPlant(data);
    } catch {
      return undefined;
    }
  }

  async addPlant(plantData: Omit<Plant, 'id' | 'userId' | 'criadoEm' | 'atualizadoEm'>): Promise<string> {
    const userId = await this.getUserId();
    if (!userId) throw new Error("Usuário não autenticado ou sessão expirada.");
    
    const processedImages: string[] = [];
    for (const img of plantData.imagens) {
      if (img.startsWith('data:')) {
        try {
          const url = await this.uploadPlantImage(userId, img);
          processedImages.push(url);
        } catch (e: any) {
          console.error("Falha no upload de imagem:", e);
          processedImages.push(img);
        }
      } else {
        processedImages.push(img);
      }
    }

    const { data, error } = await supabase
      .from('plants')
      .insert([{
        user_id: userId,
        nome_comum: plantData.nomeComum,
        nome_cientifico: plantData.nomeCientifico,
        genero: plantData.genero,
        familia: plantData.familia,
        categoria: plantData.categoria,
        reino_plantae: plantData.reinoPlantae,
        data_aquisicao: plantData.dataAquisicao || new Date().toISOString().split('T')[0],
        origem: plantData.origem,
        observacoes: plantData.observacoes,
        plano_manejo: plantData.planoManejo,
        tratamento: plantData.tratamento,
        status: plantData.status,
        localizacao: plantData.localizacao,
        imagens: processedImages,
        reminders: plantData.reminders,
        // Insert optional ultima_rega
        ultima_rega: plantData.ultimaRega,
        created_at: new Date().toISOString()
      }])
      .select()
      .single();
    
    if (error) throw new Error(`Erro de banco de dados: ${error.message}`);
    if (!data) throw new Error("Falha ao recuperar ID da planta inserida.");
    
    return data.id;
  }

  async updatePlant(id: string, plantData: Partial<Plant>): Promise<void> {
    const userId = await this.getUserId();
    if (!userId) throw new Error("Não autenticado");
    
    const updatePayload: any = {};
    if (plantData.nomeComum !== undefined) updatePayload.nome_comum = plantData.nomeComum;
    if (plantData.nomeCientifico !== undefined) updatePayload.nome_cientifico = plantData.nomeCientifico;
    if (plantData.genero !== undefined) updatePayload.genero = plantData.genero;
    if (plantData.familia !== undefined) updatePayload.familia = plantData.familia;
    if (plantData.categoria !== undefined) updatePayload.categoria = plantData.categoria;
    if (plantData.reinoPlantae !== undefined) updatePayload.reino_plantae = plantData.reinoPlantae;
    if (plantData.dataAquisicao !== undefined) updatePayload.data_aquisicao = plantData.dataAquisicao;
    if (plantData.origem !== undefined) updatePayload.origem = plantData.origem;
    if (plantData.observacoes !== undefined) updatePayload.observacoes = plantData.observacoes;
    if (plantData.planoManejo !== undefined) updatePayload.plano_manejo = plantData.planoManejo;
    if (plantData.tratamento !== undefined) updatePayload.tratamento = plantData.tratamento;
    if (plantData.status !== undefined) updatePayload.status = plantData.status;
    if (plantData.localizacao !== undefined) updatePayload.localizacao = plantData.localizacao;
    if (plantData.imagens !== undefined) updatePayload.imagens = plantData.imagens;
    if (plantData.reminders !== undefined) updatePayload.reminders = plantData.reminders;
    // Map Plant.ultimaRega to database field ultima_rega
    if (plantData.ultimaRega !== undefined) updatePayload.ultima_rega = plantData.ultimaRega;
    
    updatePayload.updated_at = new Date().toISOString();
    
    const { error } = await supabase
      .from('plants')
      .update(updatePayload)
      .eq('id', id)
      .eq('user_id', userId);
    
    if (error) throw error;
  }

  async deletePlant(id: string): Promise<void> {
    const userId = await this.getUserId();
    if (!userId) throw new Error("Não autenticado");
    const { error } = await supabase.from('plants').delete().eq('id', id).eq('user_id', userId);
    if (error) throw error;
  }

  async uploadPlantImage(userId: string, base64: string): Promise<string> {
    const path = `${userId}/plant_${Date.now()}.webp`;
    const res = await fetch(base64);
    const blob = await res.blob();
    
    const { data, error } = await supabase.storage
      .from('plants')
      .upload(path, blob, { contentType: 'image/webp' });
      
    if (error) throw error;
    const { data: { publicUrl } } = supabase.storage.from('plants').getPublicUrl(data.path);
    return publicUrl;
  }

  async parseSmartCommand(input: string): Promise<SmartActionResponse> {
    const ai = this.getAI();
    
    try {
      const response = await ai.models.generateContent({
        model: 'gemini-1.5-pro',
        contents: `Analise a consulta e forneça informações científicas detalhadas: "${input}"`,
        config: {
          systemInstruction: `Você é o Centro de Pesquisa Botânica da Raizama Garden CARE. 
          Sua missão é fornecer informações botânicas de nível científico, ligadas a bancos de dados globais.
          
          REGRAS:
          1. Se o usuário quiser agendar algo (rega, adubo, poda), defina action="create_reminder".
          2. Se for uma dúvida botânica complexa, use action="research" e forneça uma resposta detalhada.
          3. SEMPRE use o Google Search para garantir que as informações são as mais atuais do mundo.
          4. No final da sua resposta, adicione uma linha começando com "METADATA:" contendo apenas um objeto JSON com: action (string), confidence (number), e datetime (ISO string or null).`,
          tools: [{ googleSearch: {} }],
        }
      });

      console.log("[Verificação Gemini - Smart Command] Resposta bruta da IA:", JSON.stringify(response, null, 2));

      const text = response.text || '';
      const metadataMatch = text.match(/METADATA:\s*({.*})/);
      let metadata: any = { action: 'research', confidence: 0.9, datetime: null };
      
      if (metadataMatch) {
        try {
          metadata = JSON.parse(metadataMatch[1]);
        } catch (e) {
          console.error("Failed to parse AI metadata", e);
        }
      }

      let groundingChunks: any[] = [];
      try {
        groundingChunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks || [];
        console.log("[Verificação Gemini - Smart Command] Grounding recebido (parsed):", groundingChunks);
        if (!groundingChunks || groundingChunks.length === 0) {
          console.warn("[Verificação Gemini - Smart Command] Falha: Nenhum dado de Grounding foi retornado. A IA pode estar sem acesso à busca.");
        }
      } catch (e) {
         console.error("[Verificação Gemini - Smart Command] Falha estrutural ao tentar extrair Grounding:", e);
      }

      const sources = groundingChunks
          ? groundingChunks
              .filter((chunk: any) => chunk.web)
              .map((chunk: any) => ({
                uri: chunk.web.uri,
                title: chunk.web.title
              }))
          : [];

      return {
        action: metadata.action || 'research',
        message: text.split('METADATA:')[0].trim(),
        datetime: metadata.datetime || null,
        confidence: metadata.confidence || 0.9,
        sources
      };
    } catch (e) {
      console.error("[Verificação Gemini - Smart Command] Erro de API:", e);
      return {
         action: 'none',
         message: 'A busca botânica estendida está temporariamente indisponível. Por favor, tente novamente mais tarde.',
         datetime: null,
         confidence: 0,
         sources: []
      };
    }
  }

  async identifyPlantAndHealth(imageBase64: string): Promise<{ text: string; sources?: { uri: string; title: string }[] }> {
    return geminiService.analyzePlantImage(imageBase64);
  }

  async getBotanicalInfo(scientificName: string): Promise<{ family?: string; scientificName?: string; genus?: string; commonName?: string; reinoPlantae?: PlantKingdom; categoria?: PlantClassification }> {
    const ai = this.getAI();
    const response = await ai.models.generateContent({
      model: 'gemini-1.5-pro',
      contents: `Forneça informações botânicas precisas para a espécie: "${scientificName}". 
      
      REGRAS PARA O NOME POPULAR (commonName):
      Informe o nome comum mais utilizado no Brasil.
      
      REGRAS PARA O REINO PLANTAE (reinoPlantae):
      Escolha exatamente um destes valores: "Briófitas", "Pteridófitas", "Gimnospermas", "Angiospermas".
      
      REGRAS PARA A CATEGORIA (categoria):
      Escolha exatamente um destes valores baseados nas características da planta: 
      "Suculentas e cactos", "Folhagens tropicais", "Orquídeas", "Árvores e arbustos", "Plantas aquáticas", "Horta comestível", "Caudiciforme", "Medicinal".
      
      Retorne em JSON com as chaves: "family", "genus", "scientificName", "commonName", "reinoPlantae", "categoria".`,
      config: {
        responseMimeType: "application/json",
      }
    });
    try {
      return JSON.parse(response.text || '{}');
    } catch {
      return {};
    }
  }

  async suggestFertilizer(category: string, observations: string): Promise<string> {
    const ai = this.getAI();
    const response = await ai.models.generateContent({
      model: 'gemini-1.5-pro',
      contents: `Sugira o melhor adubo para uma planta da categoria "${category}". Observações: "${observations}". Retorne apenas o nome do adubo.`,
    });
    return response.text || '';
  }

  async geocodeLocation(descricao: string): Promise<{ latitude: number, longitude: number, descricao?: string } | null> {
    const ai = this.getAI();
    try {
      const response = await ai.models.generateContent({
        model: 'gemini-1.5-pro',
        contents: `Encontre as coordenadas exatas da seguinte localização: "${descricao}".
                  Retorne apenas um objeto JSON com as chaves: "latitude" (número), "longitude" (número) e "descricao" (string, o nome formatado do local encontrado).
                  Não adicione blocos de markdown, apenas o JSON puro.`,
        config: {
          tools: [{ googleMaps: {} }],
          responseMimeType: "application/json"
        }
      });
      return JSON.parse(response.text || '{}');
    } catch (e) {
      console.error("Failed to geocode location with AI", e);
      return null;
    }
  }
}
export const storage = new StorageService();
